import { useQueries, useQuery } from '@tanstack/react-query';

import { API_MAX_LIMIT } from '@/constants';
import { acceso360Client } from '@/lib/axios';
import { QueryConfig } from '@/lib/react-query';
import { requestInChunks } from '@/utils/ids';

import type { TInformationUnitTag } from '../types';

/** Every tag of the given workspaces, however many workspaces there are. */
export async function getInformationUnitTagsByWorkspaces(
  workspaceIds: string[]
): Promise<TInformationUnitTag[]> {
  const pages = await requestInChunks(workspaceIds, ids =>
    acceso360Client
      .get<TInformationUnitTag[]>('/monitoring/api/v1/information_unit_tags', {
        params: { workspace_id__in: ids.join(','), limit: API_MAX_LIMIT },
      })
      .then(({ data }) => data)
  );

  const byId = new Map<string, TInformationUnitTag>();
  pages.flat().forEach(tag => byId.set(tag.id, tag));

  return [...byId.values()];
}

type QueryFnType = typeof getInformationUnitTagsByWorkspaces;

interface UseInformationUnitTagsByWorkspacesOptions {
  workspaceIds: string[];
  config?: QueryConfig<QueryFnType>;
}

export function useInformationUnitTagsByWorkspaces({
  workspaceIds,
  config,
}: UseInformationUnitTagsByWorkspacesOptions) {
  const sortedIds = [...workspaceIds].sort();

  return useQuery({
    enabled: sortedIds.length > 0,
    ...config,
    queryKey: ['information_unit_tags', 'byWorkspaces', sortedIds],
    queryFn: () => getInformationUnitTagsByWorkspaces(sortedIds),
  });
}

export async function getInformationUnitTag(id: string): Promise<TInformationUnitTag> {
  return acceso360Client
    .get<TInformationUnitTag>(`/monitoring/api/v1/information_unit_tags/${id}`)
    .then(({ data }) => data);
}

/** Resolves tags by id, one request each: the list endpoint accepts no id filter. */
export function useInformationUnitTagsByIds(ids: string[]) {
  return useQueries({
    queries: ids.map(id => ({
      queryKey: ['information_unit_tags', 'detail', id],
      queryFn: () => getInformationUnitTag(id),
    })),
  });
}
